/**
 * Busca o histórico de treinos CONCLUÍDOS de um aluno, agrupado por dia (YYYY-MM-DD).
 *
 * Retorna um HistoryMap (data → SessionSummary[]) consumido pelo calendário e pelo
 * modal de detalhe do dia. Cada SessionSummary traz o nome do treino (plano), o nome da
 * ROTINA-pai (via workout_plans → workout_routines), a duração e a contagem de exercícios.
 *
 * Decisões não-óbvias:
 *  - duração = (completed_at − created_at) em minutos; sessão deixada "aberta" infla esse número.
 *  - exerciseCount vem de uma 2ª query (count de session_logs por sessão), não de join agregado.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { SessionSummary } from '@/lib/types';

export type HistoryMap = Record<string, SessionSummary[]>;

async function fetchHistory(studentId: string): Promise<HistoryMap> {
  const { data: sessions, error } = await supabase
    .from('workout_sessions')
    .select('id, created_at, completed_at, workout_plans(name, workout_routines(name))')
    .eq('student_id', studentId)
    .not('completed_at', 'is', null)
    .order('completed_at', { ascending: false });

  if (error || !sessions?.length) return {};

  const sessionIds = sessions.map((s: any) => s.id);
  const { data: logs } = await supabase
    .from('session_logs')
    .select('session_id')
    .in('session_id', sessionIds);

  const countMap: Record<string, number> = {};
  (logs ?? []).forEach((l: any) => {
    countMap[l.session_id] = (countMap[l.session_id] ?? 0) + 1;
  });

  const map: HistoryMap = {};
  sessions.forEach((s: any) => {
    // Data LOCAL do término (não UTC). `.slice(0,10)` pegava a data em UTC, o que
    // jogava treinos da noite (ex.: 20h em UTC-4 = 00h UTC do dia seguinte) pro dia
    // errado. O calendário usa data local, então a chave precisa ser local também.
    const d = new Date(s.completed_at);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const summary: SessionSummary = {
      id: s.id,
      planName: s.workout_plans?.name ?? 'Treino',
      routineName: s.workout_plans?.workout_routines?.name ?? null,
      durationMin: Math.round(
        (new Date(s.completed_at).getTime() - new Date(s.created_at).getTime()) / 60000
      ),
      exerciseCount: countMap[s.id] ?? 0,
      completedAt: s.completed_at,
    };
    if (!map[date]) map[date] = [];
    map[date].push(summary);
  });

  return map;
}

export function useWorkoutHistory(studentId: string) {
  const { data: historyMap = {}, isLoading: loading, refetch } = useQuery({
    queryKey: ['workout-history', studentId],
    queryFn: () => fetchHistory(studentId),
    enabled: !!studentId,
  });

  return { historyMap, loading, refetch };
}
